import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * MOXIE: drawer mobile custom con tabs persistentes (Fase 2 del header custom).
 *
 * Controla la copia mobile de blocks/_custom_menu.liquid: apertura/cierre del drawer,
 * selección de tab (un tab por ítem de menú, panel compartido con el dropdown desktop)
 * y drill-down de columnas a pantalla completa con botón "back".
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - El details exterior (summary = hamburguesa).
 * @property {HTMLDivElement} drawer - El panel deslizable del drawer.
 *
 * @extends {Component<Refs>}
 */
class CustomHeaderDrawer extends Component {
  requiredRefs = ['details', 'drawer'];

  /** @type {HTMLElement | null} */
  #searchModal = null;

  /** @type {number | null} */
  #headerStickyTop = null;

  /** @type {number | null} */
  #headerBottom = null;

  /** @type {ResizeObserver | null} */
  #headerResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('keydown', this.#onKeyDown);
    this.#searchModal = document.getElementById('search-modal');
    this.#searchModal?.addEventListener('dialog:open', this.#onSearchOpen);
    this.#searchModal?.addEventListener('dialog:close', this.#onSearchClose);
    this.#dedupeIds();
    this.#activateFirstTab();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#searchModal?.removeEventListener('dialog:open', this.#onSearchOpen);
    this.#searchModal?.removeEventListener('dialog:close', this.#onSearchClose);
    this.#searchModal = null;
    this.#headerResizeObserver?.disconnect();
  }

  /**
   * MOXIE: mide `#header-component` (no `.header-section`, que colapsa a height:0 por tener un
   * único hijo `position:absolute`) ANTES de que el scroll-lock del drawer desconecte su
   * `position:sticky` — ver `#lockHeaderPosition`. Público y atado vía `on:click` declarativo:
   * un `addEventListener` imperativo sobre `<summary>`/`<details>` no disparaba de forma
   * confiable en este entorno; la vía delegada de component.js (la misma de `selectTab`) sí.
   * @param {Event} [event]
   */
  captureHeaderMetrics(event) {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) {
      this.#headerStickyTop = null;
      this.#headerBottom = null;
      return;
    }
    const rect = header.getBoundingClientRect();
    this.#headerStickyTop = rect.top;
    this.#headerBottom = rect.bottom;
  }

  /**
   * Congela .header-section en su posición real pre-lock (no-op si todavía no llegó a pegarse)
   * y fija el offset del panel al borde inferior real del header medido en captureHeaderMetrics.
   */
  #lockHeaderPosition() {
    const section = document.querySelector('.header-section');
    const top = this.#headerStickyTop;
    if (section instanceof HTMLElement && typeof top === 'number' && top <= 0) {
      section.style.setProperty('position', 'fixed');
      section.style.setProperty('inset-inline', '0');
      section.style.setProperty('top', `${top}px`);
    }

    if (typeof this.#headerBottom === 'number') {
      this.refs.drawer.style.setProperty('--custom-menu-drawer-top', `${this.#headerBottom}px`);
    }
  }

  #unlockHeaderPosition() {
    const section = document.querySelector('.header-section');
    section?.style.removeProperty('position');
    section?.style.removeProperty('inset-inline');
    section?.style.removeProperty('top');
    this.refs.drawer.style.removeProperty('--custom-menu-drawer-top');
  }

  /**
   * MOXIE: en una carga en frío (banner de arriba o fuentes todavía asentando), la medida tomada
   * al click puede quedar vieja para cuando el drawer ya está abierto. Reobservar los hijos de
   * `#header-group` (banner + header — el group en sí tiene `display: contents`, sin caja propia
   * que ResizeObserver pueda detectar) mientras el drawer sigue abierto dispara una recorrección.
   *
   * Solo remide y reaplica — sin soltar el freeze primero. Soltarlo (probado y revertido) rompe
   * el caso scrolleado: con el scroll-lock activo `position:sticky` sigue roto, así que remedir
   * en ese estado da la posición de documento sin pegar (un `top` gigante y negativo, como si
   * scrollY fuera real) y el header/panel se congelan fuera de pantalla. Sin soltar el freeze,
   * remedir `#header-component` ya cubre los dos casos que importan: si está congelado (`top`
   * quedó fijo), su propio alto interno (fuente/ícono) sigue reflejándose bien en el `bottom`; si
   * todavía no llegó a pegarse (arriba de la página, sin congelar), no hay nada que soltar — el
   * `position:sticky` nativo ya sigue el flujo normal y ya refleja solo el banner creciendo.
   */
  #observeHeaderResize() {
    const headerGroup = document.getElementById('header-group');
    if (!(headerGroup instanceof HTMLElement)) return;
    this.#headerResizeObserver ??= new ResizeObserver(this.#recalculateHeaderPosition);
    for (const child of headerGroup.children) {
      // box:'border-box' — el default ('content-box') no cambia si lo que crece es el padding
      // (p.ej. un banner con más padding vertical), que es justo el caso que hay que detectar.
      this.#headerResizeObserver.observe(child, { box: 'border-box' });
    }
  }

  #recalculateHeaderPosition = () => {
    if (!this.isOpen) return;
    this.captureHeaderMetrics();
    this.#lockHeaderPosition();
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  get #items() {
    return Array.from(this.querySelectorAll('.custom-menu-item'));
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /** Muta solo el atributo — handleDetailsToggle (on:toggle) es la única fuente de #onOpened(). */
  open() {
    this.captureHeaderMetrics();
    this.refs.details.setAttribute('open', '');
  }

  #onOpened() {
    this.#closeDrill();
    this.#activateFirstTab();
    this.#lockHeaderPosition();
    this.#observeHeaderResize();
    requestAnimationFrame(() => {
      this.refs.details.classList.add('menu-open');
      trapFocus(this.refs.details);
    });
  }

  /**
   * Activa el primer ítem del menú (ROPA) — tab y panel visibles al abrir el drawer.
   */
  #activateFirstTab() {
    const [firstItem] = this.#items;
    if (firstItem instanceof HTMLElement) {
      this.#activateTab(firstItem);
    }
  }

  #onClosed() {
    const summary = this.refs.details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    this.refs.details.classList.remove('menu-open');
    this.#closeDrill();
    this.#unlockHeaderPosition();
    this.#headerResizeObserver?.disconnect();
    removeTrapFocus();
    summary?.focus();
  }

  /** Al abrir search-modal, soltar trap del drawer (el input vive fuera de details). */
  #onSearchOpen = () => {
    if (this.isOpen) removeTrapFocus();
  };

  /** Si el drawer sigue abierto al cerrar search, re-atrapar foco. */
  #onSearchClose = () => {
    if (this.isOpen) {
      requestAnimationFrame(() => trapFocus(this.refs.details));
    }
  };

  /**
   * Maneja la apertura/cierre nativos del `<details>` (clic en el summary o teclado). Público y
   * atado vía `on:toggle` declarativo — ver nota en `captureHeaderMetrics`.
   * @param {Event} [event]
   */
  handleDetailsToggle(event) {
    if (this.isOpen) {
      const summary = this.refs.details.querySelector('summary');
      summary?.setAttribute('aria-expanded', 'true');
      this.#onOpened();
    } else {
      this.#onClosed();
    }
  }

  /** Muta solo el atributo — handleDetailsToggle (on:toggle) es la única fuente de #onClosed(). */
  close() {
    this.refs.details.removeAttribute('open');
  }

  /**
   * Activa el tab del ítem clickeado.
   *
   * MOXIE: el markup declarativo es `on:click="/selectTab"` (sin `?data`), así que
   * assets/component.js solo pasa UN argumento — el evento. Una firma `(_target, event)`
   * hace que el evento caiga en `_target` y `event` llegue `undefined`, matando el handler
   * en silencio (bug encontrado en sesión de QA — el drawer abría pero los tabs no reaccionaban).
   * @param {Event} [event]
   */
  selectTab(event) {
    const item = event?.target instanceof Element ? event.target.closest('.custom-menu-item') : null;
    if (!(item instanceof HTMLElement)) return;

    this.#closeDrill();
    this.#activateTab(item);
  }

  /**
   * Abre el subpanel (drill-down) de la columna clickeada.
   * @param {Event} [event]
   */
  drillDown(event) {
    const column = event?.target instanceof Element ? event.target.closest('.custom-menu-column') : null;
    if (!(column instanceof HTMLElement)) return;

    column.setAttribute('data-drill-open', '');
    column.querySelector('.custom-menu-column__drill-row')?.setAttribute('aria-expanded', 'true');
    this.refs.drawer.classList.add('custom-menu-drawer--sub-open');

    const back = column.querySelector('.custom-menu-column__back');
    if (back instanceof HTMLElement) back.focus();
  }

  /**
   * Cierra el subpanel abierto y devuelve el foco a la fila que lo abrió.
   * @param {Event} [event]
   */
  drillBack(event) {
    const column = event?.target instanceof Element ? event.target.closest('[data-drill-open]') : null;
    this.#closeDrill();

    const row = column?.querySelector('.custom-menu-column__drill-row');
    if (row instanceof HTMLElement) row.focus();
  }

  /**
   * Scroll al carrusel de cards del grupo (fila "Por ocasiones de uso" en ROPA mobile).
   * @param {Event} [event]
   */
  scrollToCards(event) {
    const group = event?.target instanceof Element ? event.target.closest('.custom-menu-card-group') : null;
    const cards = group?.querySelector('.custom-menu-card-group__cards');
    if (cards instanceof HTMLElement) {
      cards.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * MOXIE: no-ops obligatorios. Los <li> de los ítems traen on:focus/blur/pointerenter/
   * pointerleave="/activate|/deactivate" (contrato del mega menu desktop, assets/header-menu.js).
   * Dentro del drawer esos eventos declarativos resuelven contra este componente — sin estos
   * métodos cada toque/foco lanzaría un error en consola.
   */
  activate() {}
  deactivate() {}

  /**
   * @param {HTMLElement} item
   */
  #activateTab(item) {
    for (const candidate of this.#items) {
      const active = candidate === item;
      candidate.toggleAttribute('data-active', active);
      candidate.querySelector('.custom-menu-item__tab')?.setAttribute('aria-expanded', String(active));
    }
    // MOXIE: el carrusel conserva scrollLeft entre tabs/aperturas; siempre arranca en la primera card.
    for (const track of item.querySelectorAll('.custom-menu-card-group__cards')) {
      track.scrollLeft = 0;
    }
  }

  #closeDrill() {
    this.refs.drawer.classList.remove('custom-menu-drawer--sub-open');
    for (const column of this.querySelectorAll('[data-drill-open]')) {
      column.removeAttribute('data-drill-open');
      column.querySelector('.custom-menu-column__drill-row')?.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const openColumn = this.querySelector('[data-drill-open]');
    if (openColumn) {
      this.#closeDrill();
      const row = openColumn.querySelector('.custom-menu-column__drill-row');
      if (row instanceof HTMLElement) row.focus();
    } else {
      this.close();
    }
  };

  /**
   * Navegación por flechas entre los tabs (foco enfocado en el tab bar).
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || !target.classList.contains('custom-menu-item__tab')) return;

    const tabs = Array.from(this.querySelectorAll('.custom-menu-item__tab'));
    const index = tabs.indexOf(target);
    if (index === -1) return;

    let next = null;
    if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    if (event.key === 'Home') next = tabs[0];
    if (event.key === 'End') next = tabs[tabs.length - 1];

    if (next instanceof HTMLElement) {
      event.preventDefault();
      next.focus();
    }
  };

  /**
   * MOXIE: el mismo static block se renderiza dos veces (desktop + drawer), así que los ids de
   * los paneles (aria-controls de los links/tabs) quedarían duplicados en el DOM. Se re-prefijan
   * los del subárbol del drawer al conectarse para restaurar la unicidad.
   */
  #dedupeIds() {
    for (const element of this.querySelectorAll('[id]')) {
      if (element.id.startsWith('drawer-')) continue;
      element.id = `drawer-${element.id}`;
    }
    for (const element of this.querySelectorAll('[aria-controls]')) {
      const value = element.getAttribute('aria-controls');
      if (!value || value.startsWith('drawer-')) continue;
      element.setAttribute('aria-controls', `drawer-${value}`);
    }
  }
}

if (!customElements.get('custom-header-drawer')) {
  customElements.define('custom-header-drawer', CustomHeaderDrawer);
}
